import React, { useEffect, useState } from "react";
import { format, parseISO, isAfter, isBefore } from "date-fns";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { cn } from "@/lib/utils";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";
import { SubmissionResponse } from "@/components/submission/types";
import MenuBar from "@/components/MenuBar";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Menu } from "lucide-react";
import { Loader2, Info } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

interface Props {}

export const EmployeeSubmissions: React.FC<Props> = () => {
  const [submissions, setSubmissions] = useState<SubmissionResponse[]>([]);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedSubmissionId, setSelectedSubmissionId] = useState<
    number | null
  >(null);
  const [rejectReason, setRejectReason] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [reopeningId, setReopeningId] = useState<number | null>(null);
  const [approvingId, setApprovingId] = useState<number | null>(null);
  const [isRejecting, setIsRejecting] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [latestSubmissionDate, setLatestSubmissionDate] = useState<Date | null>(
    null,
  );
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const handleApprove = async (submissionId: number) => {
    try {
      setApprovingId(submissionId);
      await ApiService.request(EndpointKeys.APPROVE_SUBMISSION, {
        method: "POST",
        urlParams: { submissionId },
      });

      toast({
        title: "Success",
        description: "Submission approved successfully",
      });

      fetchSubmissions();
    } catch (err) {
      toast({
        title: "Error",
        description:
          err instanceof Error ? err.message : "Failed to approve submission",
        variant: "destructive",
      });
    } finally {
      setApprovingId(null);
    }
  };

  const handleReject = async () => {
    if (!selectedSubmissionId) return;

    try {
      setIsRejecting(true);
      await ApiService.request(EndpointKeys.REJECT_SUBMISSION, {
        method: "POST",
        urlParams: { submissionId: selectedSubmissionId },
        body: { reason: rejectReason },
      });

      toast({
        title: "Success",
        description: "Submission rejected successfully",
      });

      setRejectDialogOpen(false);
      setSelectedSubmissionId(null);
      setRejectReason("");
      fetchSubmissions();
    } catch (err) {
      toast({
        title: "Error",
        description:
          err instanceof Error ? err.message : "Failed to reject submission",
        variant: "destructive",
      });
    } finally {
      setIsRejecting(false);
    }
  };

  const handleReopen = async (submissionId: number) => {
    try {
      setReopeningId(submissionId);

      await ApiService.request(EndpointKeys.REOPEN_SUBMISSION_ADMIN, {
        method: "POST",
        urlParams: { id: submissionId },
      });

      toast({
        title: "Success",
        description:
          "Submission reopened successfully. All rejected items have been restored to pending status for the nanny to edit.",
      });

      fetchSubmissions();
    } catch (err) {
      toast({
        title: "Error",
        description:
          err instanceof Error ? err.message : "Failed to reopen submission",
        variant: "destructive",
      });
    } finally {
      setReopeningId(null);
    }
  };

  const fetchSubmissions = async () => {
    try {
      setIsLoading(true);
      const response = await ApiService.request<SubmissionResponse[]>(
        EndpointKeys.GET_EMPLOYEE_SUBMISSIONS,
      );
      setSubmissions(response);

      // Find the latest submission date
      if (response.length > 0) {
        const dates = response.map((sub) => parseISO(sub.submission_month));
        const latestDate = dates.reduce((latest, current) =>
          isAfter(current, latest) ? current : latest,
        );
        setLatestSubmissionDate(latestDate);
      }
    } catch (err) {
      toast({
        title: "Error",
        description:
          err instanceof Error ? err.message : "Failed to fetch submissions",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewSubmission = (submissionId: number) => {
    navigate(`/submission/${submissionId}`);
  };

  // Function to check if a submission is the latest for a specific nanny
  const isLatestSubmission = (submission: SubmissionResponse): boolean => {
    if (!latestSubmissionDate) return false;
    const submissionDate = parseISO(submission.submission_month);
    return (
      submission.status === "REJECTED" &&
      submissionDate.getTime() === latestSubmissionDate.getTime()
    );
  };

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background">
      {/* Mobile Menu Toggle */}
      <div className="fixed top-0 left-0 right-0 z-30 flex items-center bg-gray-900 p-4 md:hidden">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="text-white"
        >
          <Menu className="h-6 w-6" />
        </Button>
      </div>

      {/* Sidebar */}
      <div
        className={`${
          isMobileMenuOpen ? "block" : "hidden"
        } fixed h-full w-64 bg-gray-900 md:relative md:block md:w-64`}
      >
        <MenuBar />
      </div>

      {/* Main Content */}
      <div className="flex-1 w-full md:w-auto mt-16 md:mt-0">
        <div className="p-4 md:p-6">
          <Card>
            <CardHeader className="pb-0 space-y-6">
              <CardTitle>Monthly Submissions</CardTitle>
              <Alert className="my-6">
                <Info className="h-4 w-4 text-primary" />
                <AlertTitle>Submission Overview</AlertTitle>
                <AlertDescription>
                  Every pay period, we automatically create a form for your
                  nanny to fill out. We collect their time off taken, any hours
                  logged, and expenses and update your payrun automatically. If
                  you don't see any submissions that is because you haven't
                  invited your nanny to Hirelo yet or they haven't signed up.
                  Please navigate to{" "}
                  <a href="https://payroll.hirelo.io/employee-management">
                    Employee Management
                  </a>{" "}
                  and click into your Nanny and navigate to Employee Invites to
                  check the status or invite your nanny!
                </AlertDescription>
              </Alert>
            </CardHeader>
            <CardContent>
              <ScrollArea className="w-full">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Month</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="hidden md:table-cell">
                          Submitted
                        </TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {submissions.map((submission) => (
                        <TableRow key={submission.id}>
                          <TableCell>
                            {format(
                              parseISO(submission.submission_month),
                              "MMM yyyy",
                            )}
                          </TableCell>
                          <TableCell>
                            <span
                              className={cn(
                                "rounded-full px-2 py-1 text-xs font-medium",
                                {
                                  "bg-yellow-100 text-yellow-800":
                                    submission.status === "SUBMITTED",
                                  "bg-green-100 text-green-800":
                                    submission.status === "APPROVED",
                                  "bg-red-100 text-red-800":
                                    submission.status === "REJECTED",
                                  "bg-gray-100 text-gray-800":
                                    submission.status === "DRAFT",
                                },
                              )}
                            >
                              {submission.status}
                            </span>
                          </TableCell>
                          <TableCell className="hidden md:table-cell">
                            {submission.submitted_at
                              ? format(
                                  new Date(submission.submitted_at),
                                  "PP pp",
                                )
                              : "Not submitted"}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col space-y-2 sm:flex-row sm:space-x-2 sm:space-y-0">
                              <Button
                                onClick={() =>
                                  handleViewSubmission(submission.id)
                                }
                                variant="outline"
                                size="sm"
                              >
                                View
                              </Button>
                              {submission.status === "SUBMITTED" && (
                                <>
                                  <Button
                                    onClick={() => handleApprove(submission.id)}
                                    variant="outline"
                                    size="sm"
                                    disabled={approvingId === submission.id}
                                    data-testid="approve-submission-button"
                                  >
                                    {approvingId === submission.id ? (
                                      <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Approving...
                                      </>
                                    ) : (
                                      "Approve"
                                    )}
                                  </Button>
                                  <Button
                                    onClick={() => {
                                      setSelectedSubmissionId(submission.id);
                                      setRejectDialogOpen(true);
                                    }}
                                    variant="outline"
                                    size="sm"
                                    disabled={approvingId === submission.id}
                                    data-testid="reject-submission-button"
                                  >
                                    Reject
                                  </Button>
                                </>
                              )}
                              {submission.status === "REJECTED" &&
                                isLatestSubmission(submission) && (
                                  <Button
                                    onClick={() => handleReopen(submission.id)}
                                    variant="default"
                                    size="sm"
                                    className="bg-secondary hover:bg-secondary/90 text-secondary-foreground"
                                    disabled={reopeningId === submission.id}
                                  >
                                    {reopeningId === submission.id ? (
                                      <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Reopening...
                                      </>
                                    ) : (
                                      "Reopen for Nanny"
                                    )}
                                  </Button>
                                )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Submission</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting this submission
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Enter rejection reason..."
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            className="min-h-[100px]"
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRejectDialogOpen(false)}
              disabled={isRejecting}
              data-testid="cancel-reject-button"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={!rejectReason || isRejecting}
              data-testid="confirm-reject-button"
            >
              {isRejecting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Rejecting...
                </>
              ) : (
                "Reject"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeeSubmissions;
